import React from 'react';
import styled from 'styled-components';

import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants';
import {
  formatSpecialsCardDaysLeft,
  getSpecialsCardDaysLeft,
  SPECIALS_CARD_ENDING_SOON_THRESHOLD_DAYS,
} from '@dutchie/common/utils/specials-card';

// Preview-only mirror of the marketplace SpecialOfferCard v2 design
// (apps/marketplace/src/components/special-offer-card/index.tsx), rendered
// behind the same redesign gate used by the storefront (ecomm.specials-redesign.rollout
// + showSpecialsRedesign + DPOS integration). Deliberate temporary duplication until the
// full card is extracted into packages/common (ECOMM-11235 / PR #20853 pattern).
// Static preview: no Link/imgix, plain div + img.

type OfferCardV2Props = {
  menuDisplayName?: string | null;
  menuDisplayImage?: string | null;
  endStamp?: string | null;
};

export const TEST_ID_OFFER_CARD_V2 = 'offer-card-v2';

export function OfferCardV2({ menuDisplayName, menuDisplayImage, endStamp }: OfferCardV2Props): JSX.Element {
  const daysLeft = getSpecialsCardDaysLeft(endStamp);
  const isEndingSoon = daysLeft !== null && daysLeft <= SPECIALS_CARD_ENDING_SOON_THRESHOLD_DAYS;

  return (
    <Container data-testid={TEST_ID_OFFER_CARD_V2}>
      <ImageSection>
        <CardImage src={menuDisplayImage ?? DEFAULT_SPECIALS_CARD_IMG} alt={menuDisplayName ?? 'Special offer'} />
      </ImageSection>

      <TextContent>
        <Title>{menuDisplayName}</Title>
        {daysLeft !== null && (
          <CountdownRow>
            <CountdownText>{formatSpecialsCardDaysLeft(daysLeft)}</CountdownText>
            {isEndingSoon && <EndingSoonBadge>Ending soon!</EndingSoonBadge>}
          </CountdownRow>
        )}
      </TextContent>
    </Container>
  );
}

const Container = styled.div`
  background: #ffffff;
  border: 1px solid #e3e7e9;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 234px;
  padding: 16px;
  width: 358px;
`;

const ImageSection = styled.div`
  aspect-ratio: 286 / 124;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
`;

const CardImage = styled.img`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.p`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #121516;
  display: -webkit-box;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  overflow: hidden;
`;

const CountdownRow = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 8px;
  letter-spacing: 0.06px;
`;

const CountdownText = styled.span`
  color: #485055;
  white-space: nowrap;
`;

const EndingSoonBadge = styled.span`
  color: #f35000;
  font-weight: 500;
`;
